<template>
  <router-view></router-view>
</template>

<script setup lang="ts">
  import { handleError } from "@/api/handleError";
  import { useCustomerIO } from "@/composables/useCustomerIO";
  import { fetchUnbundledUserAndBusinessDetails } from "@/helpers/business";
  import { IS_PROD, publicRoutes } from "@/helpers/constants";
  import { trackAmplitudeEvent } from "@/helpers/integrations/analytics/amplitude";
  import { AMPLITUDE_EVENTS } from "@/helpers/integrations/analytics/events";
  import logger from "@/helpers/logger";
  import { useAuthStore } from "@/stores/auth.store";
  import { useBusinessStore } from "@/stores/businesses.store";
  import { useUsersStore } from "@/stores/users.store";
  import { onMounted, ref, watchEffect } from "vue";
  import { useRouter } from "vue-router";

  const authStore = useAuthStore();
  const userStore = useUsersStore();

  const businessStore = useBusinessStore();

  const analytics = useCustomerIO();

  const router = useRouter();

  const isIdentified = ref(false);

  onMounted(() => {
    router.afterEach((to) => {
      try {
        const name = to.name?.toString();
        const path = to.path;

        const nameWithFallback = name ?? path.split("/").pop();

        if (IS_PROD) {
          void analytics.page(nameWithFallback ?? "");
          // trackFacebookStandardEvent(FACEBOOK_STANDARD_EVENTS.PAGE_VIEW);
        }

        void trackAmplitudeEvent(
          AMPLITUDE_EVENTS.PAGE_VIEW,
          {
            name: nameWithFallback,
            path,
          },
          true
        );
      } catch (error) {
        handleError({
          error,
          silent: true,
          fallbackMessage: `Error tracking page view: ${to.name?.toString()} - ${to.path}`,
        });
      }
    });
  });

  watchEffect(() => {
    void (async () => {
      try {
        if (!authStore.isAuthenticated || isIdentified.value) {
          return;
        }

        if (!businessStore.activeProfile?.profile) {
          logger.warn({}, "User not identified, missing user details");
          return;
        }

        const userEmail = businessStore.activeProfile?.profile?.email;

        if (!userEmail) {
          logger.warn({}, "User not identified, missing user email");
          return;
        }

        await analytics.identify(userEmail, {
          email: userEmail,
          firstName: businessStore.activeProfile?.profile?.first_name,
          lastName: businessStore.activeProfile?.profile?.last_name,
          phone: userStore?.currentUserBusinessLevel?.mobile,
        });

        // Only fetch user and business details if not on public routes
        const currentRoute = router.currentRoute.value.fullPath;
        const isNoAuthRoute = publicRoutes.some((route) =>
          currentRoute.includes(route)
        );

        if (!isNoAuthRoute) {
          void fetchUnbundledUserAndBusinessDetails();
        }

        isIdentified.value = true;
      } catch (error) {
        handleError({
          error,
          silent: true,
        });
      }
    })();
  });
</script>
